import React, { useEffect, useState } from "react";
import { SidebarItem, getSidebarItemClasses } from "./constants";
import { useTranslation } from "react-i18next";
export interface SidebarNavItemProps {
  item: SidebarItem;
  isCollapsed?: boolean;
  onItemClick?: (item: SidebarItem) => void;
  userPermissions?: string[];
}

const SidebarLinkItem: React.FC<{
  item: SidebarItem;
  isCollapsed: boolean;
  onItemClick?: (item: SidebarItem) => void;
}> = ({ item, isCollapsed, onItemClick }) => {
  const itemClasses = getSidebarItemClasses(isCollapsed);
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
    <a
      dir={isRTL ? "rtl" : "ltr"}
      href={item.href}
      className={`${itemClasses.base} ${
        item.isActive ? itemClasses.active : ""
      } gap-2`}
      onClick={(e) => {
        if (onItemClick) {
          e.preventDefault();
          onItemClick(item);
        }
      }}
    >
      {item.icon && (
        <SidebarItemIcon
          icon={item.icon}
          isActive={item.isActive}
          isCollapsed={isCollapsed}
        />
      )}
      <SidebarItemLabel label={item.label} isCollapsed={isCollapsed} />
    </a>
  );
};

const SidebarItemIcon: React.FC<{
  icon: string;
  isActive?: boolean;
  isCollapsed: boolean;
}> = ({ icon, isActive, isCollapsed }) => {
  const itemClasses = getSidebarItemClasses(isCollapsed);

  return (
    <span
      className={`${itemClasses.icon} ${
        isActive ? itemClasses.activeIcon : ""
      }`}
    >
      <img src={icon} alt="" className="w-5 h-5" />
    </span>
  );
};

const SidebarItemLabel: React.FC<{
  label?: string;
  isCollapsed: boolean;
}> = ({ label, isCollapsed }) => {
  const itemClasses = getSidebarItemClasses(isCollapsed);

  return <span className={itemClasses.label}>{label}</span>;
};

const SidebarDividerItem: React.FC = () => {
  return <hr className="my-2 border-gray-200" />;
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  isCollapsed = false,
  onItemClick,
  userPermissions = [],
}) => {
  const [shouldCollapse, setShouldCollapse] = useState(isCollapsed);

  useEffect(() => {
    const handleResize = () => {
      setShouldCollapse(window.innerWidth < 1000 || isCollapsed);
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);
  const hasPermission = item.requiredPermissions?.every((permission) =>
    userPermissions.includes(permission)
  );

  if (!hasPermission && hasPermission !== undefined) return null;
  return (
    <li>
      {item.type === "link" && (
        <SidebarLinkItem
          item={item}
          isCollapsed={shouldCollapse}
          onItemClick={onItemClick}
        />
      )}
      {item.type === "divider" && <SidebarDividerItem />}
    </li>
  );
};

export default SidebarNavItem;
